import * as React from 'react';
import Table from 'antd/lib/table';
import Result from 'antd/lib/result';
import Button from 'antd/lib/button';
import Skeleton from 'antd/lib/skeleton';
import Empty from 'antd/lib/empty';
import { MehOutlined } from '@ant-design/icons';
import { SummaryViewContextValue } from '@/utils/hooks/useSummaryView';
import { TabularLayoutType } from '@/types/summaryView';
import { antGrey } from '@/components/styles/globalStyles';

interface SummaryTableProps {
    summary: SummaryViewContextValue;
}

export const SummaryTable: React.FC<SummaryTableProps> = ({ summary }) => {
    const {
        preferences,
        shouldDisplayFetchFailed,
        setShouldDisplayFetchFailed,
        fetchSummaryView,
        isLoadingSummaryView,
        tableData,
    } = summary;

    const { tabularLayout, selectedMetricKeys, selectedVersions, selectedDestinationIds } = preferences;
    const { standardColumns, compactConfig, filteredRows } = tableData;

    const isCompactLayout = tabularLayout === TabularLayoutType.Compact;
    const tableColumns = isCompactLayout ? compactConfig.columns : standardColumns;
    const tableDataSource = isCompactLayout ? compactConfig.rows : filteredRows;

    const hasActualData = filteredRows.length > 0;

    const [hasEverLoadedData, setHasEverLoadedData] = React.useState(false);
    React.useEffect(() => {
        if (hasActualData) {
            setHasEverLoadedData(true);
        }
    }, [hasActualData]);

    const isEmptyAndLoading = !hasActualData && isLoadingSummaryView;
    const showSkeletonInEmptyState = !hasEverLoadedData || isEmptyAndLoading;

    const skeletonDataSource = React.useMemo(() => {
        if (!showSkeletonInEmptyState) return tableDataSource;
        return Array.from({ length: 8 }).map((_, index) => ({
            key: `skeleton-${index}`,
            isSkeleton: true,
        }));
    }, [showSkeletonInEmptyState, tableDataSource]);

    const columnsWithSkeleton = React.useMemo(() => {
        if (!showSkeletonInEmptyState) return tableColumns;
        return standardColumns.map((col) => ({
            ...col,
            render: (value: any, record: any, index: number) => {
                if (record.isSkeleton) {
                    return <Skeleton.Input active size="small" style={{ width: '100%' }} />;
                }
                return col.render ? col.render(value, record, index) : value;
            },
        }));
    }, [showSkeletonInEmptyState, tableColumns, standardColumns]);

    const showHeader = !(showSkeletonInEmptyState && isCompactLayout);

    return (
        <div style={{ height: '100%', width: '100%', overflow: 'auto' }}>
            {shouldDisplayFetchFailed ?
                <Result
                    icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                    subTitle="Error fetching summary view"
                    extra={
                        <Button
                            onClick={() => {
                                setShouldDisplayFetchFailed(false);
                                fetchSummaryView({
                                    destinationIds: selectedDestinationIds,
                                    versions: selectedVersions,
                                    properties: selectedMetricKeys,
                                });
                            }}
                        >
                            Try again
                        </Button>
                    }
                />
            :   <Table
                    size="small"
                    pagination={false}
                    showHeader={showHeader}
                    rowKey={(row) => row.key}
                    scroll={{ x: 'max-content' }}
                    style={{ fontSize: 6 }}
                    columns={columnsWithSkeleton}
                    dataSource={skeletonDataSource}
                    locale={{
                        emptyText: <Empty description="No data" />,
                    }}
                />
            }
        </div>
    );
};
